import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/navBar';
import { usePostgresUser } from '../components/utils/PostgresUserContext';
import Footer from '../components/footer';
import { Helmet } from 'react-helmet';
import kfHeadshot from '../images/aboutUsAssets/kf_headshot.jpg';
import steinhauerHeadshot from '../images/aboutUsAssets/steinhauer.png';
import yosukeHeadshot from '../images/aboutUsAssets/yosuke_headshot.png';

function AboutUsPage() {
    const navigate = useNavigate();
    const { user, loading: userLoading, subscriptionStatus } = usePostgresUser();
    const [language, setLanguage] = useState('en'); // 'en' for English, 'ja' for Japanese

    // Title/meta are managed by <Helmet> below (was a document.title effect that
    // raced with and clobbered the Helmet title during react-snap prerender).

    const teamMembers = [
        {
            name: "Khalid F.",
            role: {
                en: "Founder & Lead Developer",
                ja: "創設者兼リード開発者"
            },
            bio: {
                en: "With 10 years of experience in data science, algorithmic development, and technology, Khalid combined his professional expertise with his passion for language learning and interest in Japanese. He created OniKanji to provide a new learning algorithm and ed tech platform that serves as a guide to accelerate kanji learning for students worldwide.",
                ja: "データサイエンス、アルゴリズム開発、およびテクノロジーにおける10年の経験を持つKhalidは、言語学習への情熱と日本語への関心と共に彼の専門知識を組み合わせました。彼は世界中の学生の漢字学習を加速させるガイドとなる新しい学習アルゴリズムと教育テクノロジープラットフォームを提供するためにOniKanjiを創設しました。"
            },
            image: kfHeadshot,
            socialLinks: {
                linkedin: "https://www.linkedin.com/in/khalid-foflonker-6a942783/"
            }
        },
        {
            name: "Yosuke Y.",
            role: {
                en: "Head of Curriculum",
                ja: "カリキュラム責任者"
            },
            bio: {
                en: "A native Japanese speaker from Aichi Prefecture with a master's degree from Tokyo Institute of Technology, Yosuke brings both technical expertise and teaching experience to OniKanji. After a successful career at Recruit Co., Ltd. in leadership roles, he now dedicates himself to Japanese language education through his popular YouTube channel with 30,000+ subscribers, creating engaging content that makes learning Japanese accessible and enjoyable.",
                ja: "愛知県出身のネイティブ日本語話者であり、東京工業大学の修士号を持つYosukeは、技術的な専門知識と教育経験の両方をOniKanjiにもたらします。リクルート株式会社でのリーダーシップ職での成功したキャリアの後、彼は現在、3万人以上の登録者を持つ人気のYouTubeチャンネルを通じて、日本語学習をアクセスしやすく楽しいものにする魅力的なコンテンツを作成し、日本語教育に専念しています。"
            },
            image: yosukeHeadshot,
            socialLinks: {
                youtube: "https://www.youtube.com/@YosukeTeachesJapanese"
            }
        },
        {
            name: "Matt S.",
            role: {
                en: "Advisor of Pedagogy and Linguistics",
                ja: "教育学・言語学アドバイザー"
            },
            bio: {
                en: "A Ph.D Candidate at Ohio State University Department of East Asian Languages & Literature. Drawing on five years of experience teaching Japanese at Ohio State University, Matthew pioneers innovative methods for integrating advanced technologies like AI and VR into language instruction. He skillfully blends his pedagogical expertise and technological acumen with a profound enthusiasm for language acquisition and a captivating fascination with East Asian cultures and societies. Matthew's vision is to redefine the language learning journey, championing socialization as the cornerstone of second culture acquisition.",
                ja: "オハイオ州立大学東アジア言語・文学部の博士課程の候補者。オハイオ州立大学での5年間の日本語教育経験を活かし、AIやVRなどの先進技術を言語指導に統合する革新的な方法を開拓しています。彼は教育学の専門知識とテクノロジーの知識を、言語習得への深い情熱と東アジアの文化や社会への魅力的な関心と巧みに融合させています。Matthewのビジョンは、社会化を第二文化獲得の基礎として、言語学習の旅を再定義することです。"
            },
            image: steinhauerHeadshot,
            socialLinks: { website: "https://deall.osu.edu/people/steinhauer.16" }
        }
    ];

    // Content translations
    const content = {
        en: {
            pageTitle: "Meet the OniKanji Team",
            pageSubtitle: "We're a passionate group of language enthusiasts, educators, and technologists dedicated to expanding the pool of kanji learning resources for everyone.",
            joinTeam: "Join Our Team",
            joinDesc1: "We're always looking for talented individuals who are passionate about language learning and technology.",
            joinDesc2: "If you're excited about revolutionizing how people learn kanji, we'd love to hear from you!",
            sendResume: "Send Us Your Resume"
        },
        ja: {
            pageTitle: "おにかんじチーム紹介",
            pageSubtitle: "私たちは、すべての人のための漢字学習リソースの拡大に専念している言語愛好家、教育者、テクノロジストの情熱的なグループです。",
            joinTeam: "チームに参加しませんか",
            joinDesc1: "私たちは常に、言語学習とテクノロジーに情熱を持った才能ある個人を探しています。",
            joinDesc2: "漢字の学び方を革新することに興味があれば、ぜひご連絡ください！",
            sendResume: "履歴書を送る"
        }
    };

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'ja' : 'en');
    };

    return (
        <div className="font-noto-sans-jp bg-mainBackgroundColor min-h-screen">
            <Helmet>
                <title>About OniKanji — Our Team &amp; Context-First Approach to Learning Kanji</title>
                <meta name="description" content="Meet the team behind OniKanji and learn about our context-first method for mastering Japanese kanji through spaced repetition and immersion reading." />
                <link rel="canonical" href="https://onikanji.com/about-us" />
                <meta property="og:title" content="About OniKanji" />
                <meta property="og:description" content="Meet the team behind OniKanji and our context-first approach to mastering Japanese kanji through spaced repetition and immersion reading." />
                <meta property="og:image" content="https://onikanji.com/og-image.png" />
                <meta property="og:url" content="https://onikanji.com/about-us" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="OniKanji" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="About OniKanji" />
                <meta name="twitter:description" content="Meet the team behind OniKanji and our context-first approach to mastering Japanese kanji." />
                <meta name="twitter:image" content="https://onikanji.com/og-image.png" />
            </Helmet>
            <Navbar user={user} subscriptionStatus={subscriptionStatus} />

            <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
                {/* Language Toggle */}
                <div className="flex justify-end mb-6">
                    <button 
                        onClick={toggleLanguage} 
                        className="flex items-center px-4 py-2 bg-white rounded-full shadow-md text-sm font-medium text-logoColor hover:bg-gray-50 transition-colors"
                    >
                        {language === 'en' ? '日本語' : 'English'}
                        <span className="ml-2">
                            {language === 'en' ? '🇯🇵' : '🇺🇸'}
                        </span>
                    </button>
                </div>

                {/* Hero Section */}
                <div className="bg-white shadow-blueBoxShadow rounded-xl p-6 sm:p-10 mb-12">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-logoColor mb-6 text-center">
                        {content[language].pageTitle}
                    </h1>
                    <p className="text-lg sm:text-xl text-gray-700 mb-8 text-center max-w-3xl mx-auto">
                        {content[language].pageSubtitle}
                    </p>
                    
                    {/* Team Members Section */}
                    <div className="space-y-12">
                        {teamMembers.map((member, index) => (
                            <div 
                                key={index}
                                className={`bg-gray-50 rounded-xl overflow-hidden shadow-md transition-all duration-300 hover:shadow-xl ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} flex flex-col md:items-center`}
                            >
                                <div className="md:w-1/3 h-64 md:h-96 relative">
                                    <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                                    <img 
                                        src={member.image} 
                                        alt={member.name} 
                                        className="absolute inset-0 w-full h-full object-cover md:object-center transition-opacity duration-300"
                                        onLoad={(e) => e.target.previousSibling.classList.add('hidden')}
                                        loading="lazy"
                                    />
                                </div>
                                <div className="md:w-2/3 p-6 md:p-8">
                                    <h3 className="text-2xl font-bold text-logoColor">{member.name}</h3>
                                    <p className="text-darkOrangeColor font-semibold mb-3 text-lg">{member.role[language]}</p>
                                    <p className="text-gray-700 text-base mb-4 leading-relaxed">{member.bio[language]}</p>
                                    
                                    <div className="flex space-x-4 mt-6">
                                        {member.socialLinks.twitter && (
                                            <a href={member.socialLinks.twitter} target="_blank" rel="noopener noreferrer" 
                                                className="text-logoColor hover:text-logoColorHover transition-colors duration-200">
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                                                </svg>
                                            </a>
                                        )}
                                        {member.socialLinks.linkedin && (
                                            <a href={member.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" 
                                                className="text-logoColor hover:text-logoColorHover transition-colors duration-200">
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                                                </svg>
                                            </a>
                                        )}
                                        {member.socialLinks.github && (
                                            <a href={member.socialLinks.github} target="_blank" rel="noopener noreferrer" 
                                                className="text-logoColor hover:text-logoColorHover transition-colors duration-200">
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"></path>
                                                </svg>
                                            </a>
                                        )}
                                        {member.socialLinks.youtube && (
                                            <a href={member.socialLinks.youtube} target="_blank" rel="noopener noreferrer" 
                                                className="text-logoColor hover:text-logoColorHover transition-colors duration-200">
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                                </svg>
                                            </a>
                                        )}
                                        {member.socialLinks.instagram && (
                                            <a href={member.socialLinks.instagram} target="_blank" rel="noopener noreferrer" 
                                                className="text-logoColor hover:text-logoColorHover transition-colors duration-200">
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                                                </svg>
                                            </a>
                                        )}
                                        {member.socialLinks.website && (
                                            <a href={member.socialLinks.website} target="_blank" rel="noopener noreferrer" 
                                                className="text-logoColor hover:text-logoColorHover transition-colors duration-200">
                                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Join Our Team Section */}
                <div className="bg-logoColor shadow-lg rounded-xl p-6 sm:p-10 text-white overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-y-1/3 translate-x-1/4"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full translate-y-1/3 -translate-x-1/4"></div>
                    
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between">
                        <div className="md:w-2/3 mb-6 md:mb-0">
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4">{content[language].joinTeam}</h2>
                            <p className="text-lg mb-4">
                                {content[language].joinDesc1}
                            </p>
                            <p className="text-lg">
                                {content[language].joinDesc2}
                            </p>
                        </div>
                        <div className="md:w-1/3 flex justify-center md:justify-end">
                            <a 
                                href="mailto:khalid@onikanji.com" 
                                className="bg-white text-logoColor hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition duration-300 text-center shadow-md hover:shadow-lg transform hover:-translate-y-1"
                            >
                                {content[language].sendResume}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default AboutUsPage;
